import { useState, useEffect } from 'react';
import { ConsumableEvent } from '../../types';
import './Dashboard.css';

// Icons for consumable types
const TYPE_ICONS: Record<string, string> = {
  coffee: '☕',
  matcha: '🍵',
  cookie: '🍪',
  wine: '🍷',
  water: '💧',
};

// Phase display config
const PHASE_CONFIG: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'phase-active' },
  crash: { label: 'Crash', className: 'phase-crash' },
  hangover: { label: 'Hangover', className: 'phase-hangover' },
};

function formatTimeRemaining(expiresAt: string): string {
  const remaining = new Date(expiresAt).getTime() - Date.now();
  if (remaining <= 0) return '0:00';
  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function getProgress(administeredAt: string, expiresAt: string): number {
  const start = new Date(administeredAt).getTime();
  const end = new Date(expiresAt).getTime();
  const now = Date.now();
  if (now >= end) return 0;
  if (now <= start) return 1;
  return (end - now) / (end - start);
}

interface ActiveEffectsProps {
  events: ConsumableEvent[];
}

export function ActiveEffects({ events }: ActiveEffectsProps) {
  const [, setTick] = useState(0);

  // Re-render every second for countdown timers
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const active = events.filter(e => e.phase !== 'expired');

  if (active.length === 0) {
    return (
      <div className="panel active-effects-panel">
        <h3 className="panel-title">Active Effects</h3>
        <div className="no-effects">No active effects</div>
      </div>
    );
  }

  return (
    <div className="panel active-effects-panel">
      <h3 className="panel-title">Active Effects</h3>
      <div className="effects-list">
        {active.map(event => {
          const phase = PHASE_CONFIG[event.phase] ?? PHASE_CONFIG.active;
          const progress = getProgress(event.administered_at, event.expires_at);
          const timeRemaining = formatTimeRemaining(event.expires_at);

          return (
            <div key={event.id} className={`effect-item ${phase.className}`}>
              <div className="effect-header">
                <span className="effect-icon">{TYPE_ICONS[event.consumable_type] || '•'}</span>
                <span className="effect-label">
                  {event.consumable_type.charAt(0).toUpperCase() + event.consumable_type.slice(1)}
                </span>
                <span className={`effect-phase-badge ${phase.className}`}>{phase.label}</span>
                <span className="effect-timer">{timeRemaining}</span>
              </div>
              <div className="effect-progress-container">
                <div
                  className={`effect-progress-bar ${phase.className}`}
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
